type HLabel = "left" | "center" | "right" | "full" | "unknown";
type VLabel = "top" | "middle" | "bottom" | "full" | "unknown";

type Percent = `${number}%` | number;

type ViewportBandsConfig = {
  x: { left: Percent; center: Percent; right: Percent };
  y: { top: Percent; middle: Percent; bottom: Percent };
};

const DEFAULT_CONFIG: Required<Required<ViewportBandsConfig>> = {
  x: { left: "33%", center: "33%", right: "33%" },
  y: { top: "33%", middle: "33%", bottom: "33%" },
};

/**
 * Convert a number or "NN%" string to a fraction (0..1). Returns NaN if invalid.
 */
function toFraction(p: Percent): number {
  if (typeof p === "number") return p / 100;
  const n = parseFloat(String(p).trim().replace("%", ""));
  return Number.isFinite(n) ? n / 100 : NaN;
}

/**
 * Safely merge user config with defaults and validate each axis sums to 100%.
 * If sums are invalid or missing, falls back to defaults per axis.
 */
function normalizeConfig(config?: ViewportBandsConfig): Required<Required<ViewportBandsConfig>> {
  const mergedX = { ...DEFAULT_CONFIG.x, ...(config?.x ?? {}) };
  const mergedY = { ...DEFAULT_CONFIG.y, ...(config?.y ?? {}) };

  const xLeft = toFraction(mergedX.left);
  const xCenter = toFraction(mergedX.center);
  const xRight = toFraction(mergedX.right);
  const yTop = toFraction(mergedY.top);
  const yMiddle = toFraction(mergedY.middle);
  const yBottom = toFraction(mergedY.bottom);

  const isValid = (a: number, b: number, c: number) =>
    [a, b, c].every((v) => Number.isFinite(v) && v >= 0) &&
    Math.abs(a + b + c - 1) < 1e-6;

  const normX =
    isValid(xLeft, xCenter, xRight) ? { left: xLeft, center: xCenter, right: xRight }
    : {
        left: toFraction(DEFAULT_CONFIG.x.left),
        center: toFraction(DEFAULT_CONFIG.x.center),
        right: toFraction(DEFAULT_CONFIG.x.right),
      };

  const normY =
    isValid(yTop, yMiddle, yBottom) ? { top: yTop, middle: yMiddle, bottom: yBottom }
    : {
        top: toFraction(DEFAULT_CONFIG.y.top),
        middle: toFraction(DEFAULT_CONFIG.y.middle),
        bottom: toFraction(DEFAULT_CONFIG.y.bottom),
      };

  return {
    x: {
      left: `${normX.left * 100}%`,
      center: `${normX.center * 100}%`,
      right: `${normX.right * 100}%`,
    },
    y: {
      top: `${normY.top * 100}%`,
      middle: `${normY.middle * 100}%`,
      bottom: `${normY.bottom * 100}%`,
    },
  };
}

/**
 * Classify an element's position relative to the current visible viewport bands.
 *
 * Bands (by percentage of the *visible* viewport):
 *   Horizontal: left %, center %, right %
 *   Vertical:   top %,  middle %, bottom %
 *
 *  - If the element overlaps all three bands on an axis, that axis is "full".
 *  - Else if the element is fully contained within one band on an axis, use that label.
 *  - Else fall back to the element midpoint on that axis to choose the closest band.
 *
 * Returns a tuple: [horizontalLabel, verticalLabel]
 *   horizontalLabel  "left" | "center" | "right" | "full" | "unknown"
 *   verticalLabel    "top"  | "middle" | "bottom" | "full" | "unknown"
 */
export function classifyInViewport(
  el: unknown,
  config?: ViewportBandsConfig
): [HLabel, VLabel] {
  /**
   * Check For Browser
   */
  const isBrowser =
    typeof window !== "undefined" &&
    typeof document !== "undefined" &&
    typeof navigator !== "undefined";

  const isElement =
    el != null &&
    typeof el === "object" &&
    "getBoundingClientRect" in (el as any) &&
    typeof (el as any).getBoundingClientRect === "function";

  if (!isBrowser || !isElement) {
    return ["unknown", "unknown"];
  }

 /**
  * Resolve config
  */
  const normalized = normalizeConfig(config);
  const xLeftFrac = toFraction(normalized.x.left);
  const xCenterFrac = toFraction(normalized.x.center);
  const xRightFrac = toFraction(normalized.x.right);
  const yTopFrac = toFraction(normalized.y.top);
  const yMiddleFrac = toFraction(normalized.y.middle);
  const yBottomFrac = toFraction(normalized.y.bottom);

  /**
   * Grab VisualViewport
   */
  const vv = (window as any).visualViewport as
    | (VisualViewport & { offsetLeft: number; offsetTop: number })
    | undefined;

  const vpLeft = vv?.offsetLeft ?? 0;
  const vpTop = vv?.offsetTop ?? 0;
  const vpWidth =
    vv?.width ??
    window.innerWidth ??
    document.documentElement.clientWidth ??
    0;
  const vpHeight =
    vv?.height ??
    window.innerHeight ??
    document.documentElement.clientHeight ??
    0;

  const vpRight = vpLeft + vpWidth;
  const vpBottom = vpTop + vpHeight;

  if (vpWidth <= 0 || vpHeight <= 0) {
    return ["unknown", "unknown"];
  }

  /**
   * Get element bounding rects (causes style recalc - but should be light)
   */
  const rect = (el as Element).getBoundingClientRect();
  const elLeft = rect.left;
  const elRight = rect.right;
  const elTop = rect.top;
  const elBottom = rect.bottom;

  // Helper predicates on 1D ranges [a0, a1] and [b0, b1]
  const overlaps = (a0: number, a1: number, b0: number, b1: number) =>
    Math.max(a0, b0) < Math.min(a1, b1);
  const fullyInside = (a0: number, a1: number, b0: number, b1: number) =>
    a0 >= b0 && a1 <= b1;

  /**
   * Check each "screen section"
   */
  // Horizontal bands
  const leftBandStart = vpLeft;
  const leftBandEnd = vpLeft + xLeftFrac * vpWidth;

  const centerBandStart = leftBandEnd;
  const centerBandEnd = centerBandStart + xCenterFrac * vpWidth;

  const rightBandStart = centerBandEnd;
  const rightBandEnd = vpRight; // Implicitly xRightFrac * vpWidth

  // Vertical bands
  const topBandStart = vpTop;
  const topBandEnd = vpTop + yTopFrac * vpHeight;

  const middleBandStart = topBandEnd;
  const middleBandEnd = middleBandStart + yMiddleFrac * vpHeight;

  const bottomBandStart = middleBandEnd;
  const bottomBandEnd = vpBottom; // Implicitly yBottomFrac * vpHeight

  // Compute Horizontal
  const hOverlapsLeft = overlaps(elLeft, elRight, leftBandStart, leftBandEnd);
  const hOverlapsCenter = overlaps(elLeft, elRight, centerBandStart, centerBandEnd);
  const hOverlapsRight = overlaps(elLeft, elRight, rightBandStart, rightBandEnd);

  let hLabel: HLabel;
  if (hOverlapsLeft && hOverlapsCenter && hOverlapsRight) {
    hLabel = "full";
  } else if (fullyInside(elLeft, elRight, leftBandStart, leftBandEnd)) {
    hLabel = "left";
  } else if (fullyInside(elLeft, elRight, centerBandStart, centerBandEnd)) {
    hLabel = "center";
  } else if (fullyInside(elLeft, elRight, rightBandStart, rightBandEnd)) {
    hLabel = "right";
  } else {
    // Fallback: midpoint
    const midX = (elLeft + elRight) / 2;
    if (midX < leftBandEnd) hLabel = "left";
    else if (midX > rightBandStart) hLabel = "right";
    else hLabel = "center";
  }

  // Compute Vertical
  const vOverlapsTop = overlaps(elTop, elBottom, topBandStart, topBandEnd);
  const vOverlapsMiddle = overlaps(elTop, elBottom, middleBandStart, middleBandEnd);
  const vOverlapsBottom = overlaps(elTop, elBottom, bottomBandStart, bottomBandEnd);

  let vLabel: VLabel;
  if (vOverlapsTop && vOverlapsMiddle && vOverlapsBottom) {
    vLabel = "full";
  } else if (fullyInside(elTop, elBottom, topBandStart, topBandEnd)) {
    vLabel = "top";
  } else if (fullyInside(elTop, elBottom, middleBandStart, middleBandEnd)) {
    vLabel = "middle";
  } else if (fullyInside(elTop, elBottom, bottomBandStart, bottomBandEnd)) {
    vLabel = "bottom";
  } else {
    // Fallback: midpoint
    const midY = (elTop + elBottom) / 2;
    if (midY < topBandEnd) vLabel = "top";
    else if (midY > bottomBandStart) vLabel = "bottom";
    else vLabel = "middle";
  }

  return [hLabel, vLabel];
}
``